// Historial local del hilo de conversación: persiste en el navegador hasta
// 30 días y sobrevive expiraciones de sesión/conversación del server (el
// hilo visible nunca se pierde). Cada mensaje guardado lleva su convId: al
// rehidratar del server, los mensajes de la conversación actual se
// reemplazan por los del server (fuente de verdad) y los de conversaciones
// anteriores quedan como prefijo del hilo.

const STORAGE_KEY = "flux.history.v1";
const RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_MESSAGES = 200;

function defaultStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// Proyección estable de un mensaje del store: sin estados transitorios
// ("sending" muere con la página → queda "failed" para permitir reintento).
function toStored(m, convId) {
  const entry = {
    key: m.key,
    convId: m.convId || convId || null,
    sender: m.sender,
    text: m.text,
    ts: m.ts || Date.now()
  };
  if (m.id) entry.id = m.id;
  if (m.buttons) entry.buttons = m.buttons;
  if (m.list) entry.list = m.list;
  if (m.csat) entry.csat = m.csat;
  if (m.csatVote != null) entry.csatVote = m.csatVote;
  if (m.status) entry.status = m.status === "sending" ? "failed" : m.status;
  // Referencia al adjunto (nunca sus bytes): la card se re-renderiza al recargar
  // y la descarga se pide al server, que valida sesión y vencimiento.
  if (m.attachment) entry.attachment = m.attachment;
  if (m.isAttachment) entry.isAttachment = true;
  return entry;
}

export function createLocalHistory({ storage, now = Date.now } = {}) {
  const store = storage !== undefined ? storage : defaultStorage();

  function load() {
    if (!store) return [];
    try {
      const raw = store.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.messages)) return [];
      const cutoff = now() - RETENTION_MS;
      return parsed.messages
        .filter((m) => m && typeof m.text === "string" && m.key && Number(m.ts) > cutoff)
        .map((m) => (m.status === "sending" ? { ...m, status: "failed" } : m));
    } catch {
      return [];
    }
  }

  function save(messages, convId = null) {
    if (!store || !Array.isArray(messages)) return;
    const cutoff = now() - RETENTION_MS;
    const entries = messages
      .filter((m) => m && typeof m.text === "string" && m.key && (m.ts || now()) > cutoff)
      .slice(-MAX_MESSAGES)
      .map((m) => toStored(m, convId));
    const write = (list) => store.setItem(STORAGE_KEY, JSON.stringify({ v: 1, messages: list }));
    try {
      write(entries);
    } catch {
      // Storage lleno (QuotaExceeded): descartar la mitad vieja y reintentar una vez.
      try {
        write(entries.slice(Math.ceil(entries.length / 2)));
      } catch {
        // Sin persistencia disponible: la app sigue funcionando en memoria.
      }
    }
  }

  function clear() {
    try {
      store?.removeItem(STORAGE_KEY);
    } catch {
      // noop
    }
  }

  return { load, save, clear };
}

const defaultHistory = createLocalHistory();
export const loadLocalHistory = defaultHistory.load;
export const saveLocalHistory = defaultHistory.save;
export const clearLocalHistory = defaultHistory.clear;
